import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, Plus } from 'lucide-react';
import { API } from '../api';
import EntityHeader, { EntityHeaderSkeleton } from '../ui/EntityHeader';
import EntityPage, { EntityLoadProblem, EntityTabs, TabTable, clearFiltersAction, useEntityTab } from '../ui/EntityPage';
import DataTable from '../ui/DataTable';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import Menu from '../ui/Menu';
import { SeverityBadge } from '../ui/Badge';
import { formatCount, formatDate, plural, teamLabel } from '../ui/format';
import useAsync from '../ui/useAsync';
import usePagedQuery from '../ui/usePagedQuery';
import useUrlFilters from '../ui/useUrlFilters';
import EntityTickets, { fetchEntityTickets } from './EntityTickets';
import FindingsTab, { fetchFindingsCount } from './FindingsTab';
import { TEAM_OPTIONS } from './ticketFields';
import CreateTicketsPanel from './CreateTicketsPanel';
import ExportButton from './ExportPanel';
import { shortDigest } from './imageFields';

const TAB_IDS = ['findings', 'hosts', 'images', 'packages', 'tickets'];
const muted = (text) => <span className="text-graphite">{text}</span>;
const fixedIn = (version) => (version ? <span className="font-mono text-id text-ok">{version}</span> : muted('No fix yet'));
/**
 * The CVE as a whole: the list endpoint's row for it (counts, affected package
 * versions) when the keyword search finds it, otherwise the first finding.
 */
async function loadCve(cve) {
  const [summaryResult, findingsResult, hostsResult, imagesResult] = await Promise.all([
    API.GET_VULNS({ kw: cve, limit: 20 }),
    API.GET_VULN_FINDINGS({ cve, limit: 1, track: false }),
    API.GET_AFFECTED_HOSTS_FOR_CVE({ cve, limit: 1, track: false }).catch(() => null),
    API.GET_AFFECTED_IMAGES_FOR_VULN({ vulnId: cve, limit: 1, track: false }).catch(() => null),
  ]);
  const same = (id) => id?.toLowerCase() === cve.toLowerCase();
  const summary = summaryResult.data.find((v) => same(v.vuln_id) || same(v.cve)) || null;
  const finding = findingsResult.data[0] || null;
  const imageCount = imagesResult ? imagesResult.total : summary?.affected_image_count;
  if (!summary && !finding && !imageCount) {
    const error = new Error(`${cve} not found`);
    error.status = 404;
    throw error;
  }
  return {
    cve: summary?.cve || finding?.cve || cve,
    hasHosts: summary ? summary.has_hosts : Boolean(finding),
    hasImages: summary ? summary.has_images : imageCount > 0,
    hostSeverity: summary?.host_severity,
    imageSeverity: summary?.image_severity,
    imageCount,
    severity: summary?.severity || finding?.severity,
    category: summary?.category || finding?.category,
    description: summary?.description || finding?.vuln_description,
    published: summary?.published_at || finding?.vuln_published_at,
    references: (summary?.reference_url || finding?.vuln_reference || '').split(',').map((u) => u.trim()).filter(Boolean),
    // Same source as the Affected hosts tab, so header and tab counts agree.
    hostCount: hostsResult ? hostsResult.total : summary?.affected_host_count,
    packageCount: summary?.affected_package_count,
    packages: summary?.affected_packages || [],
  };
}

// Affected package versions with ids, so each row links to its package page.
async function loadAffectedPackages(packages) {
  const names = [...new Set(packages.map((p) => p.name))];
  const versionLists = await Promise.all(names.map((name) => API.GET_VERSIONS_FOR_PACKAGE({ packageName: name, limit: 500 })));
  const known = versionLists.flatMap((result) => result.data || []);
  return packages.flatMap((p) => {
    const matches = known.filter((k) => k.name === p.name && k.version === p.version);
    if (matches.length === 0) return [{ ...p, key: `${p.name}@${p.version}` }];
    return matches.map((m) => ({ ...p, id: m.id, arch: m.package_arch, vendor: m.vendor, key: m.id }));
  });
}

function fixFact(packages) {
  const withFix = packages.filter((p) => p.fixed_version).length;
  if (packages.length === 0) return null;
  if (withFix === packages.length) return <span className="text-ok">Available</span>;
  if (withFix === 0) return 'Not available';
  return `For ${withFix} of ${packages.length} versions`;
}

const hostOf = (url) => {
  try {
    return new URL(url).hostname;
  } catch (_err) {
    return null;
  }
};

function ReferencesButton({ references }) {
  if (references.length === 0) return null;
  const isNvd = (url) => url.includes('nvd.nist.gov');
  if (references.length === 1) {
    return (
      <Button href={references[0]}>
        {isNvd(references[0]) ? 'NVD' : 'Reference'}
        <ArrowUpRight size={14} className="text-graphite" aria-hidden="true" />
      </Button>
    );
  }
  const items = references.map((url, i) => ({ key: url, label: isNvd(url) ? 'NVD' : hostOf(url) || `Reference ${i + 1}`, href: url }));
  return (
    <Menu items={items} label="References" align="end">
      References
      <ChevronDown size={14} className="text-graphite" aria-hidden="true" />
    </Menu>
  );
}

// One row per host with active findings for the CVE; package-level rows are in the Findings tab.
const HOST_COLUMNS = [
  { key: 'host', header: 'Host', hideable: false, render: (h) => <EntityLink kind="host" id={h.host_id} name={h.hostname} /> },
  { key: 'os', header: 'OS', render: (h) => [h.os_name, h.os_version].filter(Boolean).join(' ') || muted('Unknown') },
  { key: 'team', header: 'Team', render: (h) => teamLabel(h.team_flow) || muted('Not set') },
  { key: 'region', header: 'Region', render: (h) => h.region || muted('—') },
  { key: 'admin', header: 'Admin', render: (h) => h.host_admin || muted('Not set') },
  { key: 'state', header: 'State', render: (h) => (h.state ? h.state.charAt(0).toUpperCase() + h.state.slice(1) : muted('—')) },
  { key: 'findings', header: 'Findings', align: 'right', render: (h) => formatCount(h.finding_count) },
];

const PACKAGE_COLUMNS = [
  { key: 'package', header: 'Package', render: (p) => <EntityLink kind="package" id={p.id} name={p.name} /> },
  { key: 'installed', header: 'Version', mono: true, render: (p) => p.version },
  { key: 'arch', header: 'Architecture', render: (p) => p.arch || muted('—') },
  { key: 'vendor', header: 'Vendor', render: (p) => p.vendor || muted('—') },
  { key: 'fixed', header: 'Fixed in', render: (p) => fixedIn(p.fixed_version) },
];

const HOST_FILTER_DEFAULTS = { host: '', team: '' };
const HOST_FILTERS = [
  { key: 'host', type: 'search', label: 'Host', placeholder: 'Hostname', mono: true, width: 240 },
  { key: 'team', type: 'select', label: 'Team', options: [{ value: '', label: 'All' }, ...TEAM_OPTIONS] },
];

function HostsTab({ cve }) {
  const [filters, setFilters] = useUrlFilters(HOST_FILTER_DEFAULTS);
  const key = `${cve}|${filters.host}|${filters.team}`;
  const params = { cve, kw: filters.host, team: filters.team };
  const fetchPage = useCallback((paging) => API.GET_AFFECTED_HOSTS_FOR_CVE({ ...params, ...paging }), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = usePagedQuery(fetchPage, key);

  return (
    <TabTable
      actions={<ExportButton query={{ endpoint: 'affected_hosts_for_cve', params }} />}
      label={`Hosts affected by ${cve}`}
      filters={HOST_FILTERS}
      values={filters}
      onFiltersChange={setFilters}
      query={query}
      filtered={Boolean(filters.host || filters.team)}
      columns={HOST_COLUMNS}
      rowKey={(h) => h.host_id}
      errorTitle="Affected hosts could not be loaded"
      empty={<EmptyState title="No active hosts are affected">Hosts appear here while they report a vulnerable package version.</EmptyState>}
      filteredEmpty={(
        <EmptyState title="No affected hosts match these filters" action={clearFiltersAction(() => setFilters(HOST_FILTER_DEFAULTS))}>
          The host filter matches part of the hostname. To filter by package, use the Findings tab.
        </EmptyState>
      )}
    />
  );
}

const IMAGE_COLUMNS = [
  { key: 'image', header: 'Container image', hideable: false, render: (i) => <EntityLink kind="image" id={i.image_id} name={i.image_name} title={i.image_key} /> },
  { key: 'repository', header: 'Repository', mono: true, render: (i) => i.repository || muted('—') },
  { key: 'digest', header: 'Current digest', mono: true, render: (i) => (i.current_digest ? <span title={i.current_digest}>{shortDigest(i.current_digest)}</span> : muted('Unknown')) },
  { key: 'severity', header: 'Severity', render: (i) => <SeverityBadge severity={i.severity} /> },
  {
    key: 'packages',
    header: 'Packages',
    mono: true,
    grow: true,
    minWidth: 180,
    fullText: (i) => (i.package_names || []).join(', '),
    render: (i) => (i.package_names || []).join(', ') || muted('—'),
  },
  { key: 'findings', header: 'Findings', align: 'right', render: (i) => formatCount(i.finding_count) },
];

const IMAGE_FILTER_DEFAULTS = { image: '' };
const IMAGE_FILTERS = [{ key: 'image', type: 'search', label: 'Container image', placeholder: 'Name or path', mono: true, width: 260 }];

function ImagesTab({ cve }) {
  const [filters, setFilters] = useUrlFilters(IMAGE_FILTER_DEFAULTS);
  const key = `${cve}|${filters.image}`;
  const fetchPage = useCallback((paging) => API.GET_AFFECTED_IMAGES_FOR_VULN({ vulnId: cve, kw: filters.image, ...paging }), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = usePagedQuery(fetchPage, key);

  return (
    <TabTable
      label={`Container images affected by ${cve}`}
      filters={IMAGE_FILTERS}
      values={filters}
      onFiltersChange={setFilters}
      query={query}
      filtered={Boolean(filters.image)}
      columns={IMAGE_COLUMNS}
      rowKey={(i) => i.image_id}
      errorTitle="Affected container images could not be loaded"
      empty={<EmptyState title="No container images are affected">Container images appear here while the current digest’s last scan reports this vulnerability.</EmptyState>}
      filteredEmpty={(
        <EmptyState title="No affected container images match this filter" action={clearFiltersAction(() => setFilters(IMAGE_FILTER_DEFAULTS))}>
          The filter matches part of the container image path.
        </EmptyState>
      )}
    />
  );
}

function PackagesTab({ packages }) {
  const key = packages.map((p) => `${p.name}@${p.version}`).join(',');
  const { data, loading, error, retry } = useAsync(() => loadAffectedPackages(packages), key);
  return (
    <DataTable
      className="flex-1"
      label="Affected package versions"
      columns={PACKAGE_COLUMNS}
      rows={data || []}
      rowKey={(p) => p.key}
      loading={loading}
      error={error}
      onRetry={retry}
      errorTitle="Affected packages could not be loaded"
      empty={<EmptyState title="No package versions recorded">The scanner reported this CVE without naming the affected package versions.</EmptyState>}
    />
  );
}

export default function CvePage() {
  const { cve: rawCve } = useParams();
  const cve = decodeURIComponent(rawCve);
  const entity = useAsync(() => loadCve(cve), cve);
  const [urlTab, setTab] = useEntityTab(TAB_IDS);
  const { search } = useLocation();
  // A vulnerability found only on container images opens on its images tab.
  const tab = !new URLSearchParams(search).has('tab') && entity.data && !entity.data.hasHosts ? 'images' : urlTab;
  const [panelOpen, setPanelOpen] = useState(false);
  const [ticketsVersion, setTicketsVersion] = useState(0);
  const tickets = useAsync(() => fetchEntityTickets({ cve }, { limit: 1 }), `${cve}|${ticketsVersion}`);
  const findings = useAsync(() => fetchFindingsCount({ cve }), cve);
  const crumbs = [{ label: 'Vulnerabilities', to: '/vulnerabilities' }, { label: cve }];
  const v = entity.data;

  const facts = useMemo(() => v && [
    { label: 'Severity', value: <SeverityBadge severity={v.severity} /> },
    // Each source rates the vulnerability itself; show both when they can differ.
    ...(v.hasHosts && v.hasImages ? [
      { label: 'On hosts', value: <SeverityBadge severity={v.hostSeverity} /> },
      { label: 'On container images', value: <SeverityBadge severity={v.imageSeverity} /> },
    ] : []),
    { label: 'Category', value: v.category },
    { label: 'Published', value: formatDate(v.published) },
    // Fix and package facts come from host packages, so an image-only vulnerability has none.
    ...(v.hasHosts ? [
      { label: 'Fix', value: fixFact(v.packages) },
      { label: 'Affected hosts', value: v.hostCount !== undefined ? plural(v.hostCount, 'host') : null },
    ] : []),
    ...(v.hasImages ? [{ label: 'Affected container images', value: v.imageCount !== undefined ? plural(v.imageCount, 'container image') : null }] : []),
    ...(v.hasHosts ? [{ label: 'Affected packages', value: v.packageCount !== undefined ? plural(v.packageCount, 'version') : null }] : []),
  ], [v]);

  if (entity.error) {
    return (
      <EntityLoadProblem
        crumbs={crumbs}
        noun="vulnerability"
        identifier={cve}
        error={entity.error}
        onRetry={entity.retry}
        listPath={`/vulnerabilities?kw=${encodeURIComponent(cve)}`}
        listLabel="Search Vulnerabilities"
      />
    );
  }

  return (
    <EntityPage>
      {!v ? (
        <EntityHeaderSkeleton crumbs={crumbs} identifier={cve} />
      ) : (
        <EntityHeader
          crumbs={crumbs}
          identifier={v.cve}
          severity={v.severity}
          summary={v.description}
          facts={facts}
          actions={(
            <>
              {v.hasHosts && (
                <Button onClick={() => setPanelOpen(true)}>
                  <Plus size={15} strokeWidth={2} className="text-graphite" aria-hidden="true" />
                  Create tickets
                </Button>
              )}
              <ReferencesButton references={v.references} />
            </>
          )}
        />
      )}
      <CreateTicketsPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        lock={{ type: 'cve-based', cve: v?.cve || cve }}
        onCreated={({ created }) => {
          setTicketsVersion((n) => n + 1);
          if (created > 0) setTab('tickets');
        }}
      />
      <EntityTabs
        label={`${cve} details`}
        value={tab}
        onChange={setTab}
        tabs={[
          {
            id: 'findings',
            label: v?.hasImages ? 'Host findings' : 'Findings',
            count: findings.data,
            render: () => <FindingsTab scope={{ cve }} hide={['cve']} label={`Findings for ${cve}`} />,
          },
          { id: 'hosts', label: 'Affected hosts', count: v?.hostCount, render: () => <HostsTab cve={cve} /> },
          { id: 'images', label: 'Affected container images', count: v?.imageCount, render: () => <ImagesTab cve={cve} /> },
          { id: 'packages', label: 'Affected packages', count: v?.packages.length, render: () => (v ? <PackagesTab packages={v.packages} /> : <DataTable className="flex-1" label="Affected package versions" columns={PACKAGE_COLUMNS} rows={[]} loading />) },
          {
            id: 'tickets',
            label: 'Tickets',
            count: tickets.data?.total,
            render: () => (
              <EntityTickets
                filter={{ cve }}
                version={ticketsVersion}
                emptyTitle={`No tickets for ${cve}`}
                emptyText="Tickets that include this CVE appear here."
                emptyAction={<Button onClick={() => setPanelOpen(true)}>Create tickets</Button>}
              />
            ),
          },
        ]}
      />
    </EntityPage>
  );
}
